import { VIEW_ENTITIES } from '@shared/types/view';
import type { ValueOf } from '@shared/types/utilities';
import type { TFilterConfig } from '@shared/features/views/types/filter-config';
import {
  createdAtFilterConfig,
  birthDateFilterConfig,
  leadChannelFilterConfig,
  leadDuplicatesFilterConfig,
  leadEnrollmentStatusFilterConfig,
  leadEnglishLevelFilterConfig,
  leadGenderFilterConfig,
  leadNextFollowUpDateFilterConfig,
  leadRankFilterConfig,
  leadScreeningMeetingDateFilterConfig,
  leadUpdatedAtFilterConfig,
  programStatusFilterConfig,
  taskDueDateFilterConfig,
  taskPriorityFilterConfig,
  taskStatusFilterConfig,
  labelFilterConfig,
  leadCalendlyTypeFilterConfig,
  leadReferalFilterConfig,
  leadStatusFilterConfig,
  membersFilterConfig,
  programFilterConfig,
  invoiceStatusFilterConfig,
  invoiceDueDateFilterConfig,
  paymentMethodFilterConfig,
  paymentDateFilterConfig,
  productTypeFilterConfig,
  partnerFilterConfig,
  enrolledDateFilterConfig,
} from '@shared/features/views/configs';

type FilterEntry =
  | TFilterConfig
  | ((viewEntityType?: ValueOf<typeof VIEW_ENTITIES>) => TFilterConfig | null);

const filterRegistry = new Map<string, FilterEntry>();

filterRegistry.set('created_at_before:created_at_after', createdAtFilterConfig);
filterRegistry.set('birth_date_before:birth_date_after', birthDateFilterConfig);
filterRegistry.set('channel', leadChannelFilterConfig);
filterRegistry.set('duplicates', leadDuplicatesFilterConfig);
filterRegistry.set('enrollment_status', leadEnrollmentStatusFilterConfig);
filterRegistry.set('english_level', leadEnglishLevelFilterConfig);
filterRegistry.set('gender', leadGenderFilterConfig);
filterRegistry.set(
  'next_follow_up_date_before:next_follow_up_date_after',
  leadNextFollowUpDateFilterConfig
);
filterRegistry.set('rank', leadRankFilterConfig);
filterRegistry.set(
  'screening_meeting_date_before:screening_meeting_date_after',
  leadScreeningMeetingDateFilterConfig
);
filterRegistry.set(
  'updated_at_before:updated_at_after',
  leadUpdatedAtFilterConfig
);
// due_date_before:due_date_after is now entity-aware (see accounting section below)
filterRegistry.set('priority', taskPriorityFilterConfig);
filterRegistry.set('label', labelFilterConfig);
filterRegistry.set('labels', labelFilterConfig);
filterRegistry.set('calendly_event_type', leadCalendlyTypeFilterConfig);
filterRegistry.set('referal', leadReferalFilterConfig);
filterRegistry.set('program', programFilterConfig);
filterRegistry.set('programs', programFilterConfig);
filterRegistry.set('partner', partnerFilterConfig);
filterRegistry.set(
  'enrolled_date_before:enrolled_date_after',
  enrolledDateFilterConfig
);

filterRegistry.set('assignee', () =>
  membersFilterConfig({ facetName: 'assignee' })
);
filterRegistry.set('reporter', () =>
  membersFilterConfig({ facetName: 'reporter' })
);
filterRegistry.set('watcher', () =>
  membersFilterConfig({ facetName: 'watcher' })
);

// -- Accounting / product filters --
// due_date is shared between task and invoice — pick the right config per entity
filterRegistry.set('due_date_before:due_date_after', (viewEntityType) => {
  if (viewEntityType === VIEW_ENTITIES.invoice)
    return invoiceDueDateFilterConfig;
  return taskDueDateFilterConfig;
});
filterRegistry.set('method', paymentMethodFilterConfig);
filterRegistry.set(
  'payment_date_before:payment_date_after',
  paymentDateFilterConfig
);
filterRegistry.set('type', productTypeFilterConfig);

filterRegistry.set('status', (viewEntityType) => {
  if (viewEntityType === VIEW_ENTITIES.lead) return leadStatusFilterConfig;
  if (viewEntityType === VIEW_ENTITIES.task) return taskStatusFilterConfig;
  if (viewEntityType === VIEW_ENTITIES.program)
    return programStatusFilterConfig;
  if (viewEntityType === VIEW_ENTITIES.invoice)
    return invoiceStatusFilterConfig;
  return leadStatusFilterConfig;
});

export const getFilterConfigByFacetName = (
  facetName: string,
  viewEntityType?: ValueOf<typeof VIEW_ENTITIES>
): TFilterConfig | null => {
  const entry = filterRegistry.get(facetName);
  if (!entry) return null;
  return typeof entry === 'function' ? entry(viewEntityType) : entry;
};
